import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  InlineAlertType,
  PayOverTimeAutopayResultTypenames,
  SubmitPayOverTimeLoanDisbursementResultTypenames,
  SubmitPayOverTimeLoanDisbursementSuccess,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import PaymentScheduleTimeline from 'features/payOverTime/components/PaymentScheduleTimeline/PaymentScheduleTimeline';
import PayOverTimePaymentAccountRow from 'features/payOverTime/components/PayOverTimePaymentAccountRow/PayOverTimePaymentAccountRow';
import {
  AutopayConfirmationError,
  logPayOverTimeError,
  type PayOverTimeErrorConfig,
} from 'features/payOverTime/helpers/PayOverTime.helpers';
import usePayOverTimeAgreementDocument from 'features/payOverTime/hooks/usePayOverTimeAgreementDocument';
import { useSetAutopayForPayOverTime } from 'features/payOverTime/hooks/useSetAutopayForPayOverTime';
import useSubmitPayOverTimeLoanDisbursement from 'features/payOverTime/hooks/useSubmitPayOverTimeLoanDisbursement';
import usePaymentPlanAccount from 'features/payOverTime/screens/application/PaymentPlan/hooks/usePaymentPlanAccount';
import PaymentPlanWhatIsAutopayBottomSheet from 'features/payOverTime/screens/application/PaymentPlan/PaymentPlanWhatIsAutopayBottomSheet';
import {
  agreementIdAtom,
  paymentPlanDetailsAtom,
  registrationInfoIdAtom,
  transactionIdAtom,
} from 'features/payOverTime/store/payOverTimeOnboardingStore';
import { useAtomValue } from 'jotai';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import FullScreenLoading from 'shared/components/vxl/loading/FullScreenLoading/FullScreenLoading';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Checkbox from 'shared/components/vxl/selection/Checkbox/Checkbox';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import HyperlinkBaseText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import {
  useViewDidFocusScreenAnalyticsEvent,
  useViewDidFocusTrackAnalyticsEvent,
} from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useBackHandler } from 'shared/hooks/useBackHandler/useBackHandler';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { ErrorSeverity } from 'shared/utils/sentry/sentry.constants';
import { formatDate } from 'shared/utils/string/date';

const PaymentPlan = () => {
  const router = useRouter();
  const [isAutoPayEnabled, setIsAutoPayEnabled] = useState(true);
  const [isAgreed, setIsAgreed] = useState(false);
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const errorAnalyticsLoggedRef = useRef(false);
  const whatIsAutopayBottomSheetRef = useRef<BottomSheetModal>(null);
  const { t: translatedText } = useTranslation('payOverTime', {
    keyPrefix: 'paymentPlan',
  });
  const { bottom } = useSafeAreaInsets();
  const dismissToTab = useDismissToRootTab();
  const { analyticsEvents } = useAnalyticsConfiguration();
  const { transactionId: routeTransactionId } = useLocalSearchParams<{
    transactionId?: string;
  }>();
  const storedTransactionId = useAtomValue(transactionIdAtom);
  const transactionId = routeTransactionId ?? storedTransactionId;
  const paymentPlanDetails = useAtomValue(paymentPlanDetailsAtom);
  const registrationInfoId = useAtomValue(registrationInfoIdAtom);
  const agreementId = useAtomValue(agreementIdAtom);
  const { account: paymentAccount, fetching: isAccountLoading } =
    usePaymentPlanAccount();
  const {
    submitLoanDisbursement,
    fetching: isSubmitting,
    errorModel: disbursementErrorModel,
  } = useSubmitPayOverTimeLoanDisbursement();
  const { setAutopay, fetching: isSettingAutopay } =
    useSetAutopayForPayOverTime();
  const { viewTruthInLendingDocument } = usePayOverTimeAgreementDocument();
  const isSubmittingPlan = isSubmitting || isSettingAutopay;
  const isMissingPlanState =
    !paymentPlanDetails ||
    !transactionId ||
    !registrationInfoId ||
    !agreementId;
  const hasDisbursementError =
    Boolean(disbursementErrorModel) || hasSubmitError;
  const hasErrorState = isMissingPlanState || hasDisbursementError;
  const errorName = isMissingPlanState ? 'data_validation' : 'server_issue';
  useViewDidFocusScreenAnalyticsEvent(
    hasErrorState ? null : { key: '/pay-over-time/payment-plan' },
  );

  const goBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      dismissToTab(Tabs.HOME);
    }
  };

  useBackHandler(goBack);

  const handleTruthInLendingPress = () => {
    viewTruthInLendingDocument();
  };

  const handleAutopayToggle = (enabled: boolean) => {
    setIsAutoPayEnabled(enabled);
  };

  const handleWhatIsAutopayPress = () => {
    whatIsAutopayBottomSheetRef.current?.present();
  };

  useEffect(() => {
    if (!hasErrorState) {
      errorAnalyticsLoggedRef.current = false;
      return;
    }

    if (errorAnalyticsLoggedRef.current) {
      return;
    }

    errorAnalyticsLoggedRef.current = true;

    void analyticsEvents.logScreenEventFromConfig(
      'payOverTimeApplicationFlowErrorEncountered',
      { error_name: errorName },
    );
  }, [analyticsEvents, errorName, hasErrorState]);

  const errorTrackAnalytics = hasErrorState
    ? {
        name: 'Pay Over Time Application Flow Error Encountered',
        customAttributes: {
          category: 'pay_over_time',
          source_name: 'pay_over_time_application_error_encountered',
          error_name: errorName,
        },
      }
    : null;
  useViewDidFocusTrackAnalyticsEvent(errorTrackAnalytics);

  const completeDisbursement = async (
    result: SubmitPayOverTimeLoanDisbursementSuccess,
  ) => {
    if (!paymentPlanDetails) {
      return;
    }

    const loanId = result.disbursementId;
    // Not redundant on the ON path. bnpl-service sets autoPay=true in runCatching
    // blocks that log and swallow failures, and disbursement still returns success.
    // Readers resolve the column as `loan.autoPay ?: false`, and the disbursement
    // result exposes only disbursementId, so a silently-failed server write would
    // present as OFF to a customer who was shown ON. Remove only once the
    // disbursement response reports autopay state.
    const autopayResult = await setAutopay({
      loanId,
      enabled: isAutoPayEnabled,
    });

    const isAutopayConfirmed =
      autopayResult?.__typename ===
        PayOverTimeAutopayResultTypenames.PayOverTimeAutopaySuccess &&
      autopayResult.autoPayEnabled === isAutoPayEnabled;

    const unconfirmedError = isAutoPayEnabled
      ? AutopayConfirmationError.UnconfirmedOn
      : AutopayConfirmationError.UnconfirmedOff;
    const autopayConfirmationError = isAutopayConfirmed
      ? undefined
      : unconfirmedError;

    if (autopayConfirmationError) {
      logPayOverTimeError(
        new Error(
          'Pay Over Time autopay could not be confirmed after disbursement',
        ),
        {
          transactionId,
          loanId,
          requestedAutopayEnabled: isAutoPayEnabled,
        },
        ErrorSeverity.CRITICAL,
      );
    }

    const transactionAmount = paymentPlanDetails.lineItems[0]?.value ?? '';
    const firstPayment = paymentPlanDetails.paymentSchedule.scheduleItems[0];

    router.navigate({
      pathname: '/pay-over-time/application-success',
      params: {
        amount: transactionAmount,
        firstPaymentAmount: firstPayment?.amount.formatted ?? '',
        firstPaymentDate: firstPayment
          ? formatDate(firstPayment.dueDate, 'MMMM d')
          : '',
        ...(autopayConfirmationError
          ? { autopayError: autopayConfirmationError, loanId }
          : undefined),
      },
    });
  };

  const handleAcceptPlan = async () => {
    if (!paymentPlanDetails) {
      return;
    }

    const result = await submitLoanDisbursement({
      transactionId,
      registrationInfoId,
      agreementId,
    });

    if (!result) {
      return;
    }

    switch (result.__typename) {
      case SubmitPayOverTimeLoanDisbursementResultTypenames.SubmitPayOverTimeLoanDisbursementSuccess:
        await completeDisbursement(result);
        break;
      case SubmitPayOverTimeLoanDisbursementResultTypenames.SubmitPayOverTimeLoanDisbursementError:
        setHasSubmitError(true);
        break;
      default:
        break;
    }
  };

  const dismissErrorToHome = () => dismissToTab(Tabs.HOME);

  const resolveErrorConfig = (): PayOverTimeErrorConfig => {
    const doneButton = {
      title: translatedText('error.button'),
      onPress: dismissErrorToHome,
    };

    if (isMissingPlanState) {
      return {
        screenType: ErrorScreenType.Error,
        messageHeadline: translatedText('error.title'),
        messageBody: translatedText('error.body'),
        primaryButton: doneButton,
      };
    }

    return {
      screenType: disbursementErrorModel?.screenType ?? ErrorScreenType.Error,
      messageHeadline:
        disbursementErrorModel?.messageHeadline ??
        translatedText('error.title'),
      messageBody:
        disbursementErrorModel?.messageBody ?? translatedText('error.body'),
      primaryButton: doneButton,
    };
  };

  if (hasErrorState) {
    const errorConfig = resolveErrorConfig();

    return (
      <View style={styles.screen}>
        <Stack.Screen
          options={{
            headerShown: false,
            gestureEnabled: false,
          }}
        />
        <ErrorScreen
          screenType={errorConfig.screenType}
          messageHeadline={errorConfig.messageHeadline}
          messageBody={errorConfig.messageBody}
          primaryButton={errorConfig.primaryButton}
        />
      </View>
    );
  }

  const paymentScheduleItems =
    paymentPlanDetails?.paymentSchedule.scheduleItems.map((item) => ({
      id: `${item.dueDate}-${item.amount.amount}`,
      label: item.label,
    })) ?? [];

  return (
    <View style={[styles.screen, { paddingBottom: bottom + theme.spacing.m }]}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          gestureEnabled: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} primaryOnPress={goBack} />
          ),
        }}
      />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollViewContent}
      >
        <View style={styles.headerSection}>
          <TitleLgText numberOfLines={0}>{translatedText('title')}</TitleLgText>
          <BodyBaseText numberOfLines={0}>
            {paymentPlanDetails.summaryHeadline}
          </BodyBaseText>
        </View>

        <View>
          {paymentPlanDetails.lineItems.map((item, index) => {
            const lastItem = paymentPlanDetails.lineItems.length - 1;
            const isLastItem = index === lastItem;
            return (
              <Fragment key={`line-item-${item.label}`}>
                <ListItem
                  primaryCopy={item.label}
                  secondaryCopy={item.subLabel ?? undefined}
                  lineFormat={
                    item.subLabel ? ListItemLineFormat.CAPTION : undefined
                  }
                  showsChevron={false}
                  trailingAccessory={{
                    id: `line-item-value-${item.label}`,
                    name: VXLComponentId.LIST_ITEM_TEXT_STACK,
                    props: {
                      primaryCopy: item.value,
                      boldPrimaryCopy: isLastItem,
                    },
                  }}
                  boldPrimaryCopy={isLastItem}
                />
                {index < lastItem ? (
                  <View style={styles.lineItemDivider}>
                    <HairLineDivider />
                  </View>
                ) : null}
              </Fragment>
            );
          })}
        </View>
        <View style={styles.divider} />
        <View style={styles.autopaySection}>
          <SectionDivider labelText={translatedText('paymentDetails')} />
          <ListItem
            primaryCopy={translatedText('autopayLabel')}
            showsChevron={false}
            trailingAccessory={{
              id: 'payment-plan-autopay-toggle',
              name: VXLComponentId.SWITCH,
              props: {
                isOn: isAutoPayEnabled,
                showStatusLabel: true,
                onValueChange: handleAutopayToggle,
                isDisabled: isSubmittingPlan,
              },
            }}
            trailingAccessoryContainerStyle={styles.autopayToggleContainer}
            accessibilityLabel={translatedText('autopayLabel')}
          />
          <View style={styles.lineItemDivider}>
            <HairLineDivider />
          </View>
          {isAccountLoading ? (
            <View style={styles.accountLoading}>
              <LoadingSpinner />
            </View>
          ) : null}
          {isAutoPayEnabled && !isAccountLoading && paymentAccount ? (
            <PayOverTimePaymentAccountRow
              account={paymentAccount}
              secondaryCopy={`${translatedText('available')} ${paymentAccount.availableBalance.formatted}`}
            />
          ) : null}
          <View style={styles.autopayCopyContainer}>
            <BodySmText numberOfLines={0} style={styles.autopayBodyText}>
              {translatedText('autopayDescription')}{' '}
              <HyperlinkBaseText
                onPress={handleWhatIsAutopayPress}
                analyticsId="paymentPlanWhatIsAutopayClicked"
              >
                {translatedText('whatIsAutopay')}
              </HyperlinkBaseText>
            </BodySmText>
          </View>
        </View>
        <>
          <SectionDivider
            labelText={
              paymentPlanDetails.paymentSchedule.title ??
              translatedText('paymentSchedule')
            }
          />
          <PaymentScheduleTimeline
            items={paymentScheduleItems}
            disclosureText={
              paymentPlanDetails.paymentSchedule.disclosureText ||
              translatedText('lateFeeCaption')
            }
          />
        </>
        <View style={styles.divider} />
        <View style={styles.termsSection}>
          <Checkbox
            checked={isAgreed}
            onSelectedStateChange={() => setIsAgreed(!isAgreed)}
            analyticsId="paymentPlanTermsCheckbox"
          >
            <View style={styles.termsTextContainer}>
              <BodyBaseText>
                {translatedText('agreeToTerms')}{' '}
                <HyperlinkBaseText
                  onPress={handleTruthInLendingPress}
                  analyticsId="paymentPlanDisclosureClicked"
                >
                  {translatedText('truthInLendingAndAgreement')}
                </HyperlinkBaseText>
              </BodyBaseText>
            </View>
          </Checkbox>
          <View style={styles.alertSection}>
            <InlineAlert
              alertType={InlineAlertType.Info}
              bodyText={translatedText('acceptanceWarning')}
              isImageVisible={false}
            />
          </View>
        </View>
      </ScrollView>

      <View style={styles.buttonContainer}>
        <Button
          label={translatedText('acceptPlan')}
          analyticsId="paymentPlanAcceptClicked"
          onPress={handleAcceptPlan}
          type="PRIMARY"
          width="FULL"
          isDisabled={!isAgreed || isSubmittingPlan}
        />
        <Button
          label={translatedText('cancel')}
          analyticsId="paymentPlanCancelClicked"
          onPress={goBack}
          type="SECONDARY"
          width="FULL"
        />
      </View>

      <ScreenTransitionOverlay
        isLoading={isSubmittingPlan}
        LoadingComponent={
          <FullScreenLoading
            isCoinLoader
            bodyText={translatedText('loadingBody')}
          />
        }
        ErrorComponent={null}
      />
      <PaymentPlanWhatIsAutopayBottomSheet
        bottomSheetRef={whatIsAutopayBottomSheetRef}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  divider: {
    backgroundColor: theme.color.background.surface,
    height: theme.spacing.s,
  },
  screen: { backgroundColor: theme.color.background.primary, flex: 1 },
  scrollView: { flex: 1 },
  scrollViewContent: { gap: theme.spacing.s },
  headerSection: {
    paddingHorizontal: theme.spacing.m,
    paddingVertical: theme.spacing.m,
    gap: theme.spacing.m,
  },
  lineItemDivider: { marginHorizontal: theme.spacing.m },
  accountLoading: { alignItems: 'center', paddingVertical: theme.spacing.m },
  autopaySection: { paddingBottom: theme.spacing.s },
  autopayToggleContainer: { flexShrink: 0 },
  autopayCopyContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  autopayBodyText: { color: theme.color.text.secondary },
  termsSection: { paddingHorizontal: theme.spacing.m },
  termsTextContainer: { flex: 1 },
  alertSection: { paddingBottom: theme.spacing.m, paddingTop: theme.spacing.s },
  buttonContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
    gap: theme.spacing.m,
    borderTopWidth: 1,
    borderTopColor: theme.color.border.primary,
  },
});

export default PaymentPlan;
